/*
 * SHER-499 — synchronous, redacted snapshot of the Redux app state at crash time.
 *
 * Answers "what state was the app in right before it failed": current route + investigation,
 * how much was loaded, what form/fields the user was on. It is attached to the exception
 * breadcrumb and the Slack crash report by the crash handlers.
 *
 * Zero-knowledge invariant: the patients and form reducers hold CLEARTEXT personal/clinical data
 * in memory. This builder is an ALLOWLIST that emits ONLY scalar shape/metadata (uuids, names of
 * the study, counts, field NAMES + a hasValue boolean) and NEVER a personal value. The output is
 * a flat object of scalars by construction, so there is no nested object/array through which a
 * stray personal field could ride along, and it survives the breadcrumb redaction allowlist
 * (client redactData + server sanitizeDataMap) unchanged.
 *
 * Pure given `state`; environment reads (navigator/localStorage/build defines) are each guarded so
 * a crash handler can call this on a half-dead page without throwing a second error.
 */

const MAX_FORM_FIELDS_CHARS = 600;

function safeOnline() {
    try {
        return typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
            ? navigator.onLine
            : undefined;
    } catch (_) { return undefined; }
}

function safeRoute(route) {
    if (typeof route === 'string') return route;
    try {
        return typeof window !== 'undefined' && window.location ? window.location.pathname : undefined;
    } catch (_) { return undefined; }
}

function hasLocalStorageKey(key) {
    try {
        const v = window.localStorage?.getItem(key);
        return typeof v === 'string' && v.length > 0;
    } catch (_) { return undefined; }
}

// Count loaded patients without touching their contents. `patients.data` is keyed by investigation
// uuid → array of patients (see patientsReducer); also tolerate a bare array or null.
function countPatients(patientsData) {
    try {
        if (!patientsData) return 0;
        if (Array.isArray(patientsData)) return patientsData.length;
        if (typeof patientsData === 'object') {
            return Object.values(patientsData).reduce(
                (acc, v) => acc + (Array.isArray(v) ? v.length : 0),
                0,
            );
        }
        return 0;
    } catch (_) { return undefined; }
}

// redux-form state: { [formName]: { values: {field: value}, registeredFields: {...} } }.
// Emit form names + "field:hasValue" pairs — NAMES and a 0/1 flag only, never the value.
function summarizeForms(formState) {
    const result = { activeForms: undefined, activeFormFields: undefined };
    try {
        if (!formState || typeof formState !== 'object') return result;
        const names = Object.keys(formState);
        if (!names.length) return result;
        result.activeForms = names.join(',');
        const pairs = [];
        for (const name of names) {
            const values = formState[name] && formState[name].values;
            if (!values || typeof values !== 'object') continue;
            for (const field of Object.keys(values)) {
                const v = values[field];
                const hasValue = v !== undefined && v !== null && v !== '' ? 1 : 0;
                pairs.push(`${field}:${hasValue}`);
            }
        }
        if (pairs.length) result.activeFormFields = pairs.join(',').slice(0, MAX_FORM_FIELDS_CHARS);
        return result;
    } catch (_) { return result; }
}

// Drop keys whose value is undefined so the snapshot stays compact and the allowlist test sees
// only meaningful fields.
function compact(obj) {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
        if (v !== undefined) out[k] = v;
    }
    return out;
}

/**
 * @param {object} state  Redux state (store.getState()).
 * @param {object} [opts] { route, appVersion, appGitSha } — overrides for env reads (route falls
 *                          back to window.location.pathname; versions to the build defines).
 * @returns {object} flat scalar snapshot.
 */
function getAppStateSnapshot(state, opts) {
    const o = opts || {};
    const s = state || {};
    const investigations = s.investigations || {};
    const current = investigations.currentInvestigation || {};
    const patients = s.patients || {};
    const offline = s.offline || {};
    const forms = summarizeForms(s.form);

    return compact({
        route: safeRoute(o.route),
        investigationUuid: typeof current.uuid === 'string' ? current.uuid : undefined,
        investigationName: typeof current.name === 'string' ? current.name : undefined,
        patientsLoaded: countPatients(patients.data),
        pendingSyncActions: Array.isArray(offline.pendingActions) ? offline.pendingActions.length : undefined,
        patientsDownloading: typeof patients.downloading === 'boolean' ? patients.downloading : undefined,
        patientsLoading: typeof patients.loading === 'boolean' ? patients.loading : undefined,
        online: safeOnline(),
        activeForms: forms.activeForms,
        activeFormFields: forms.activeFormFields,
        hasResearcherKey: hasLocalStorageKey('rawKeyResearcher'),
        hasInvestigationKey: hasLocalStorageKey('keyInvestigation'),
        appVersion: typeof o.appVersion === 'string' ? o.appVersion : undefined,
        appGitSha: typeof o.appGitSha === 'string' ? o.appGitSha : undefined,
    });
}

// Thin glue for the crash handlers: read the live store + build defines and produce the snapshot.
// Lazy-require the store so the pure getAppStateSnapshot above stays the unit-tested seam and we
// avoid an import cycle at module load. Never throws — a crash handler calls this on a failing page.
function captureAppState() {
    try {
        // eslint-disable-next-line global-require
        const store = require('../../redux/store').default || require('../../redux/store');
        const defines = {
            // __APP_VERSION__ / __GIT_SHA__ are Vite build-time defines; typeof-guard for runtimes
            // (tests) where they are not injected.
            appVersion: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined,
            appGitSha: typeof __GIT_SHA__ !== 'undefined' ? __GIT_SHA__ : undefined,
        };
        return getAppStateSnapshot(store.getState(), defines);
    } catch (_) {
        return {};
    }
}

export { getAppStateSnapshot, captureAppState };
